/* CONTEXT:START
Wave: 1
Status: implemented
Feature: Add to Report terminology - updated workflow status display with progress bar fix using isIncludedInReport
Stub-Type: mock-data
Dependencies: [SubmissionList, Updated types, Submission.isIncludedInReport field]
Risk-Level: low
Validation-Method: unit-test
Performance-Target: <1s
AWS-Profile: dev
Last-Updated: 2025-08-08
CONTEXT:END */

import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Assignment,
  Schedule,
  Edit,
  CheckCircle,
  Cancel,
  TrendingUp,
  Person,
  AccessTime,
  Add,
  Remove
} from '@mui/icons-material';
import { WorkflowState, Submission } from '../types';

interface SubmissionWorkflowStatusProps {
  submission: Submission;
  showDetails?: boolean;
  compact?: boolean;
  reportStatus?: 'draft' | 'approved' | 'distributed'; // Add report status prop
}

/**
 * Component to display workflow status for submissions
 * Shows current state, penholder assignment, and deadline information
 * Updated to use "Add to Report" terminology instead of approve/reject
 */
export const SubmissionWorkflowStatus: React.FC<SubmissionWorkflowStatusProps> = ({
  submission,
  showDetails = true,
  compact = false,
  reportStatus
}) => {
  
  const getWorkflowStateConfig = (state: WorkflowState) => {
    const configs = {
      submitted: {
        color: 'info' as const,
        icon: <Assignment />,
        label: 'Submitted',
        description: submission.penholderAssignedTo 
          ? `Pending review by penholder (${submission.penholderAssignedTo})`
          : 'Waiting for penholder assignment'
      },
      under_review: {
        color: 'warning' as const,
        icon: <Schedule />,
        label: 'Under Review',
        description: 'Being reviewed by penholder'
      },
      edited: {
        color: 'secondary' as const,
        icon: <Edit />,
        label: 'Edited',
        description: 'Edited, pending final approval'
      },
      added_to_report: {
        color: 'success' as const,
        icon: <Add />,
        label: 'Added to Report',
        description: 'Selected for inclusion in report'
      },
      not_added_to_report: {
        color: 'error' as const,
        icon: <Remove />,
        label: 'Not Added',
        description: 'Not selected for inclusion'
      },
      included_in_report: {
        color: 'primary' as const,
        icon: <TrendingUp />,
        label: 'In Report',
        description: 'Included in published report'
      }
    };
    
    return configs[state] || configs.submitted;
  };

  const calculateDaysUntilDeadline = (deadline: string): number => {
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getWorkflowProgress = (state: WorkflowState, reportStatus?: string): number => {
    // If submission has been processed for a published report, show 100%
    // CRITICAL FIX: Only return 100% if isIncludedInReport is explicitly true
    // isIncludedInReport can be false, true, or undefined - only true means processed
    if (submission.isIncludedInReport === true) {
      return 100;
    }
    
    // If report is distributed, all submissions should show 100% progress
    if (reportStatus === 'distributed') {
      return 100;
    }
    
    // If report is approved, submissions that were added to report should show 100%
    if (reportStatus === 'approved' && state === 'added_to_report') {
      return 100;
    }
    
    const progressMap = {
      submitted: 20,
      under_review: 40,
      edited: 60,
      added_to_report: 80,
      not_added_to_report: 100,
      included_in_report: 100
    };
    return progressMap[state] || 0;
  };

  const stateConfig = getWorkflowStateConfig(submission.workflowState);
  const daysUntilDeadline = submission.penholderReviewDeadline 
    ? calculateDaysUntilDeadline(submission.penholderReviewDeadline)
    : null;

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title={stateConfig.description}>
          <Chip
            icon={stateConfig.icon}
            label={stateConfig.label}
            color={stateConfig.color}
            size="small"
          />
        </Tooltip>
        {daysUntilDeadline !== null && daysUntilDeadline > 0 && (
          <Tooltip title={`Review deadline: ${new Date(submission.penholderReviewDeadline!).toLocaleDateString()}`}>
            <Chip
              icon={<AccessTime />}
              label={`${daysUntilDeadline}d`}
              color={daysUntilDeadline <= 2 ? 'error' : daysUntilDeadline <= 4 ? 'warning' : 'default'}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Main Status */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Chip
          icon={stateConfig.icon}
          label={stateConfig.label}
          color={stateConfig.color}
          size="small"
        />
        <Typography variant="caption" color="text.secondary">
          {stateConfig.description}
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Box mb={2}>
        <LinearProgress
          variant="determinate"
          value={getWorkflowProgress(submission.workflowState, reportStatus)}
          color={stateConfig.color}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {showDetails && (
        <Box>
          {/* Penholder Assignment */}
          {submission.penholderAssignedTo && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Assigned to: {submission.penholderAssignedTo}
              </Typography>
              {submission.penholderAssignedAt && (
                <Typography variant="caption" color="text.secondary">
                  on {new Date(submission.penholderAssignedAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}

          {/* Review Deadline */}
          {submission.penholderReviewDeadline && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AccessTime fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Review deadline: {new Date(submission.penholderReviewDeadline).toLocaleDateString()}
              </Typography>
              {daysUntilDeadline !== null && (
                <Chip
                  label={
                    daysUntilDeadline > 0 
                      ? `${daysUntilDeadline} days left`
                      : daysUntilDeadline === 0
                      ? 'Due today'
                      : `${Math.abs(daysUntilDeadline)} days overdue`
                  }
                  color={
                    daysUntilDeadline < 0 
                      ? 'error' 
                      : daysUntilDeadline <= 2 
                      ? 'error' 
                      : daysUntilDeadline <= 4 
                      ? 'warning' 
                      : 'default'
                  }
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {/* Version Information */}
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="caption" color="text.secondary">
              Version: {submission.currentVersion}
              {submission.currentVersion !== submission.originalVersion && (
                <span> (originally {submission.originalVersion})</span>
              )}
            </Typography>
          </Box>

          {/* Penholder Feedback */}
          {submission.penholderFeedback && (
            <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Penholder Feedback:
              </Typography>
              <Typography variant="body2">
                {submission.penholderFeedback}
              </Typography>
              {submission.penholderEditedAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Updated: {new Date(submission.penholderEditedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

          {/* Last Editor Information */}
          {submission.workflowState === 'edited' && submission.lastEditedByName && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Edit fontSize="small" color="secondary" />
              <Typography variant="caption" color="text.secondary">
                Last edited by: {submission.lastEditedByName}
                {submission.lastEditedAt && (
                  <span> on {new Date(submission.lastEditedAt).toLocaleString()}</span>
                )}
              </Typography>
            </Box>
          )}

          {/* Add to Report Status */}
          {(submission.workflowState === 'added_to_report' || submission.workflowState === 'not_added_to_report') && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {submission.addedToReport ? (
                <Add fontSize="small" color="success" />
              ) : (
                <Remove fontSize="small" color="error" />
              )}
              <Typography variant="caption" color="text.secondary">
                {submission.addedToReport ? 'Added to report' : 'Not added to report'}
              </Typography>
            </Box>
          )}

          {/* Report Inclusion Status */}
          {submission.isIncludedInReport && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <TrendingUp fontSize="small" color="primary" />
              <Typography variant="caption" color="text.secondary">
                Included in published report
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
